/**
 * Customs declaration — capture-time snapshot persisted to
 * `seller_orders.customs_declaration` (jsonb). Re-injected on the Shippo
 * **shipment** payload at label-buy (NOT on the transaction — §G probe
 * attempt 1 showed Shippo silently drops it when nested on transaction).
 *
 * Mirrors the subset of Shippo's `CustomsDeclaration` we author for the
 * US-origin MVP — fields outside this subset (`b13a_filing_option`,
 * `commercial_invoice`, `disclaimer`, …) are left to Shippo defaults.
 *
 * `eel_pfc` is OPTIONAL on the wire schema:
 *  - REQUIRED on US-origin USPS Intl ≤ $2500 (`NOEEI_30_37_a`) — see §G probe.
 *  - OMITTED for GB/EU origin (different export-compliance regime).
 *  - `AES_ITN` reserved for the future > $2500 path (out of MVP scope — the
 *    builder refuses ABOVE_AES_THRESHOLD instead of stubbing one out).
 *
 * @see .tondocs/shippo/PROBES_2026_06_25.md §G — 3 builder gates + verdict
 * @see .tondocs/shippo/ORIGIN_COUNTRY_SUPPORT.md §3 — origin-axis customs spec
 * @see lib/shipping/build-customs-declaration.ts — sole producer
 */

import { z } from "zod";
import { pinned } from "./pin";

// ---------------------------------------------------------------------------
// Per-item line on the declaration
// ---------------------------------------------------------------------------

export interface CustomsDeclarationItem {
  /** Description shown to the customs officer. Derived from listing taxonomy
   *  (`taxonomy_items.name`), NOT from listing title — titles are noisy
   *  user-generated strings. ≤ 25 chars. */
  description: string;
  /** Net weight per single unit. */
  net_weight: string;
  mass_unit: "g" | "kg" | "oz" | "lb";
  quantity: number;
  /** Major units, stringified to match Shippo's wire shape. */
  value_amount: string;
  value_currency: string;
  /** ISO 3166 alpha-2, uppercase. */
  origin_country: string;
}

// ---------------------------------------------------------------------------
// Top-level declaration
// ---------------------------------------------------------------------------

export interface CustomsDeclaration {
  contents_type: "MERCHANDISE";
  /** ≤ 25 chars (USPS hard cap, §G attempt 3). */
  contents_explanation: string;
  non_delivery_option: "RETURN";
  incoterm: "DDU";
  certify: true;
  certify_signer: string;
  /** US-origin only: `NOEEI_30_37_a` for ≤ $2500 USPS Intl. Omitted for
   *  non-US origin (different export-compliance regime). */
  eel_pfc?: "NOEEI_30_37_a";
  items: CustomsDeclarationItem[];
}

// ---------------------------------------------------------------------------
// Zod schemas — pinned to interfaces
// ---------------------------------------------------------------------------

export const CustomsDeclarationItemSchema = pinned<CustomsDeclarationItem>()(
  z.object({
    description: z.string().min(1).max(25),
    net_weight: z.string().min(1),
    mass_unit: z.enum(["g", "kg", "oz", "lb"]),
    quantity: z.number().int().positive(),
    value_amount: z.string().min(1),
    value_currency: z.string().min(3).max(3),
    origin_country: z.string().length(2),
  }),
);

export const CustomsDeclarationSchema = pinned<CustomsDeclaration>()(
  z.object({
    contents_type: z.literal("MERCHANDISE"),
    contents_explanation: z.string().min(1).max(25),
    non_delivery_option: z.literal("RETURN"),
    incoterm: z.literal("DDU"),
    certify: z.literal(true),
    certify_signer: z.string().min(1),
    eel_pfc: z.literal("NOEEI_30_37_a").optional(),
    items: z.array(CustomsDeclarationItemSchema).min(1),
  }),
);
